import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import {
  ArrowLeftIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  BellAlertIcon,
  ClockIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

// Register the image resize module
Quill.register('modules/imageResize', ImageResize);

// Searchable Select Component for Manager Selection
const SearchableSelect = ({ value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white text-left flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
      >
        <span className="truncate">{selectedOption?.label || placeholder}</span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-800">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users..."
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-[#161616] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-48">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-3 py-2 text-sm text-left transition-colors ${
                    value === option.value
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-[#222222]'
                  }`}
                >
                  <div className="truncate">{option.label}</div>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                No users found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// New Department Manager Form Component
const NewDepartmentManagerForm = ({ users, loadingUsers, onAdd }) => {
  const [formData, setFormData] = useState({
    department: 'general',
    managerId: '',
    designation: '',
    enabled: true
  });
  const [errors, setErrors] = useState({});

  const handleAdd = () => {
    // Validate
    const newErrors = {};

    // Check if managerId is provided and valid
    if (!formData.managerId || (typeof formData.managerId === 'string' && formData.managerId.trim() === '')) {
      newErrors.managerId = 'Please select a manager';
    }

    if (!formData.department) {
      newErrors.department = 'Please select a department';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Call onAdd callback with validated data
    onAdd(formData);

    // Reset form
    setFormData({
      department: 'general',
      managerId: '',
      designation: '',
      enabled: true
    });
    setErrors({});
  };

  return (
    <div className="border border-blue-200 dark:border-blue-700 rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20">
      <div className="grid grid-cols-3 gap-3 mb-3">
        {/* Department Type */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Department *
          </label>
          <select
            value={formData.department}
            onChange={(e) => {
              setFormData({ ...formData, department: e.target.value });
              setErrors({ ...errors, department: null });
            }}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
          >
            <option value="hr">HR</option>
            <option value="finance">Finance</option>
            <option value="it">IT</option>
            <option value="sales">Sales</option>
            <option value="support">Support</option>
            <option value="legal">Legal</option>
            <option value="operations">Operations</option>
            <option value="marketing">Marketing</option>
            <option value="general">General</option>
          </select>
          {errors.department && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.department}</p>
          )}
        </div>

        {/* Manager Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Manager *
          </label>
          <SearchableSelect
            value={formData.managerId}
            onChange={(value) => {
              setFormData({ ...formData, managerId: value });
              setErrors({ ...errors, managerId: null });
            }}
            options={users.map(user => ({
              value: user._id || user.id,
              label: `${user.firstName} ${user.lastName} (${user.email})`
            }))}
            placeholder={loadingUsers ? 'Loading users...' : users.length === 0 ? 'No users found' : 'Select manager'}
            disabled={loadingUsers || users.length === 0}
          />
          {errors.managerId && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.managerId}</p>
          )}
        </div>

        {/* Designation */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Designation
          </label>
          <input
            type="text"
            value={formData.designation}
            onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
            placeholder="e.g., HR Manager"
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          {formData.managerId && users.find(u => u._id === formData.managerId) && (
            <span>
              ✉️ {users.find(u => u._id === formData.managerId)?.email}
            </span>
          )}
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-1.5 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors flex items-center gap-1"
        >
          <span>+ Add Manager</span>
        </button>
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <p className="text-xs text-red-600 dark:text-red-400">
            Please fill in all required fields before adding.
          </p>
        </div>
      )}
    </div>
  );
};

// Section component defined outside to prevent re-creation on each render
const Section = ({ id, title, description, icon: Icon, children, isExpanded, onToggle, onSave, onCancel, saving, hasChanges }) => {
  return (
    <div className={`rounded-xl overflow-hidden transition-all ${isExpanded ? 'border border-gray-200 dark:border-gray-700 shadow-sm' : 'border border-gray-200 dark:border-gray-800'}`}>
      <button
        onClick={() => onToggle(id)}
        className={`w-full px-5 py-3.5 flex items-center justify-between transition-colors ${
          isExpanded
            ? 'bg-white dark:bg-[#161616] border-b border-gray-200 dark:border-gray-800'
            : 'bg-white dark:bg-[#161616] hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-100 dark:bg-[#222222]'}`}>
            <Icon className={`w-4 h-4 ${isExpanded ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
          </div>
          <div className="text-left">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && !isExpanded && (
            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
          )}
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isExpanded && (
        <div className="bg-white dark:bg-[#161616]">
          <div className="px-5 py-5">
            {children}
          </div>

          {/* Save/Cancel buttons */}
          <div className="px-5 py-3 bg-gray-50 dark:bg-[#111111] border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              disabled={saving || !hasChanges}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a1a]"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || !hasChanges}
              className="px-4 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              {saving ? (
                <>
                  <div className="w-3 h-3 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AIConfiguration = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(() => ({
    mainAgent: {
      userId: '',
      displayName: '',
      enabled: true,
      agentType: 'iris-main'
    },
    guardrails: {
      autoApprove: [],
      requireApproval: [],
      spendingLimit: 100
    },
    personality: {
      tone: 'professional',
      responseStyle: 'concise',
      customInstructions: '',
      greetingTemplate: 'Hello! I\'m Iris, your AI assistant. How can I help you today?',
      emailSignature: ''
    },
    escalation: {
      method: 'ai',
      urgencyKeywords: [],
      aiEscalation: {
        enabled: true,
        analysisFactors: ['urgency_level', 'sentiment', 'complexity', 'risk_level', 'vip_status'],
        triggers: {
          urgencyLevel: 'high',
          sentimentThreshold: -0.5,
          complexityThreshold: 75,
          riskLevel: 'high',
          minConfidence: 70
        },
        departmentRouting: {
          enabled: true,
          departments: []
        }
      },
      sentimentDetection: true,
      confidenceThreshold: 70,
      vipEmails: [],
      escalationTimeHours: 4
    },
    workingHours: {
      responseTargets: {
        standard: 4,
        urgent: 1,
        low: 24
      },
      afterHoursBehavior: 'auto_respond'
    },
    compliance: {
      gdpr: false,
      hipaa: false,
      dataRetentionDays: 90,
      auditLogging: true,
      piiHandling: 'standard'
    }
  }));
  const [originalConfig, setOriginalConfig] = useState(null); // Store original values for cancel
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    mainAgent: true,
    guardrails: false,
    personality: false,
    escalation: false,
    workingHours: false,
    compliance: false
  });
  const [savingSection, setSavingSection] = useState(null); // Track which section is being saved

  useEffect(() => {
    fetchConfig();
    fetchDomains();
    fetchUsers();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/ai/config');
      let apiData = response.data.data;
      const defaults = getDefaultConfig();

      let configData;
      if (!apiData) {
        configData = defaults;
      } else {
        // Merge API data with defaults to ensure all sections exist
        // Check if domainId is a domain name (contains '.') instead of ObjectId
        let domainId = apiData.mainAgent?.domainId || '';
        if (domainId && typeof domainId === 'string' && domainId.includes('.')) {
          // It's a domain name, not an ObjectId - clear it
          logger.warn('⚠️  Invalid domainId detected (domain name instead of ObjectId):', domainId);
          domainId = '';
        }

        // Clean up legacy neverAllow field if it exists
        if (apiData.guardrails?.neverAllow) {
          delete apiData.guardrails.neverAllow;
        }

        // Merge each section with defaults
        configData = {
          mainAgent: {
            ...defaults.mainAgent,
            ...apiData.mainAgent,
            domainId: domainId,
          },
          guardrails: {
            ...defaults.guardrails,
            ...apiData.guardrails,
          },
          personality: {
            ...defaults.personality,
            ...apiData.personality,
          },
          escalation: {
            ...defaults.escalation,
            ...apiData.escalation,
            aiEscalation: {
              ...defaults.escalation.aiEscalation,
              ...apiData.escalation?.aiEscalation,
              triggers: {
                ...defaults.escalation.aiEscalation.triggers,
                ...apiData.escalation?.aiEscalation?.triggers,
              },
              departmentRouting: {
                ...defaults.escalation.aiEscalation.departmentRouting,
                ...apiData.escalation?.aiEscalation?.departmentRouting,
              }
            }
          },
          workingHours: {
            ...defaults.workingHours,
            ...apiData.workingHours,
            responseTargets: {
              ...defaults.workingHours.responseTargets,
              ...apiData.workingHours?.responseTargets,
            }
          },
          compliance: {
            ...defaults.compliance,
            ...apiData.compliance,
          }
        };

      }

      setConfig(configData);
      setOriginalConfig(JSON.parse(JSON.stringify(configData))); // Deep clone for original
    } catch (error) {
      logger.error('Error fetching config:', error);
      showNotification?.('Failed to load configuration', 'error');
      const defaultConfig = getDefaultConfig();
      setConfig(defaultConfig);
      setOriginalConfig(JSON.parse(JSON.stringify(defaultConfig)));
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async () => {
    try {
      setLoadingDomains(true);
      const response = await axiosInstance.get('/api/business/domains');
      // Filter for verified domains only
      const verifiedDomains = (response.data.domains || []).filter(d => d.isVerified);
      setDomains(verifiedDomains);
    } catch (error) {
      logger.error('Error fetching domains:', error);
      showNotification?.('Failed to load domains', 'error');
    } finally {
      setLoadingDomains(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axiosInstance.get('/api/business/users');
      // Filter for active non-AI users only
      const activeUsers = (response.data.users || []).filter(u =>
        u.status === 'active' && !u.isAIAgent
      );
      setUsers(activeUsers);
    } catch (error) {
      logger.error('Error fetching users:', error);
      showNotification?.('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const getDefaultConfig = () => ({
    mainAgent: {
      userId: '', // User ID of the mailbox to use as AI assistant
      displayName: 'Iris AI',
      enabled: true,
      agentType: 'iris-main' // Main Iris agent type
    },
    guardrails: {
      autoApprove: ['meetings', 'internal_emails', 'basic_info'],
      requireApproval: ['external_emails', 'financial_transactions', 'access_grants'],
      spendingLimit: 100
    },
    personality: {
      tone: 'professional',
      responseStyle: 'concise',
      customInstructions: '',
      greetingTemplate: 'Hello! I\'m Iris, your AI assistant. How can I help you today?',
      emailSignature: ''
    },
    escalation: {
      method: 'ai',
      urgencyKeywords: ['urgent', 'asap', 'emergency', 'critical'],
      aiEscalation: {
        enabled: true,
        analysisFactors: ['urgency_level', 'sentiment', 'complexity', 'risk_level', 'vip_status'],
        triggers: {
          urgencyLevel: 'high',
          sentimentThreshold: -0.5,
          complexityThreshold: 75,
          riskLevel: 'high',
          minConfidence: 70
        },
        departmentRouting: {
          enabled: true,
          departments: []
        }
      },
      sentimentDetection: true,
      confidenceThreshold: 70,
      vipEmails: [],
      escalationTimeHours: 4
    },
    workingHours: {
      responseTargets: {
        standard: 4,
        urgent: 1,
        low: 24
      },
      afterHoursBehavior: 'auto_respond'
    },
    compliance: {
      gdpr: false,
      hipaa: false,
      dataRetentionDays: 90,
      auditLogging: true,
      piiHandling: 'standard'
    }
  });

  // Section-specific save handlers
  const saveSection = useCallback(async (sectionName, endpoint, data) => {
    try {
      setSavingSection(sectionName);

      const response = await axiosInstance.put(endpoint, data);

      // Update both config and originalConfig with the response
      const updatedData = response.data.data;

      setConfig(prev => ({
        ...prev,
        [sectionName]: updatedData
      }));

      setOriginalConfig(prev => ({
        ...prev,
        [sectionName]: JSON.parse(JSON.stringify(updatedData)) // Deep clone
      }));

      showNotification?.(response.data.message || 'Saved successfully', 'success');
    } catch (error) {
      logger.error(`❌ Error saving ${sectionName}:`, error);
      logger.error('Error response:', error.response?.data);
      showNotification?.(error.response?.data?.message || `Failed to save ${sectionName}`, 'error');
    } finally {
      setSavingSection(null);
    }
  }, [showNotification]);

  const saveMainAgent = () => {
    // Clean up old fields (emailPrefix, domainId) before sending
    const cleanedMainAgent = { ...config.mainAgent };
    delete cleanedMainAgent.emailPrefix;
    delete cleanedMainAgent.domainId;

    saveSection('mainAgent', '/api/ai/config/main-agent', cleanedMainAgent);
  };
  const saveGuardrails = () => saveSection('guardrails', '/api/ai/config/guardrails', config.guardrails);
  const savePersonality = () => saveSection('personality', '/api/ai/config/personality', config.personality);
  const saveEscalation = () => {
    // Validate that all departments have a managerId before saving
    const departments = config.escalation.aiEscalation.departmentRouting?.departments || [];
    const invalidDepartments = departments.filter(dept => !dept.managerId || dept.managerId.trim() === '');

    if (invalidDepartments.length > 0) {
      const deptNames = invalidDepartments.map(d => d.department.toUpperCase()).join(', ');
      showNotification?.(`Cannot save: The following departments are missing managers: ${deptNames}. Please assign managers or remove these departments.`, 'error');
      return;
    }

    // All validations passed, save the escalation config
    saveSection('escalation', '/api/ai/config/escalation', config.escalation);
  };
  const saveWorkingHours = () => saveSection('workingHours', '/api/ai/config/working-hours', config.workingHours);
  const saveCompliance = () => saveSection('compliance', '/api/ai/config/compliance', config.compliance);

  // Cancel handlers - revert to original values
  const cancelSection = useCallback((sectionName) => {
    if (originalConfig && originalConfig[sectionName]) {
      setConfig(prev => ({
        ...prev,
        [sectionName]: JSON.parse(JSON.stringify(originalConfig[sectionName]))
      }));
      showNotification?.('Changes cancelled', 'info');
    }
  }, [originalConfig, showNotification]);

  const cancelMainAgent = () => cancelSection('mainAgent');
  const cancelGuardrails = () => cancelSection('guardrails');
  const cancelPersonality = () => cancelSection('personality');
  const cancelEscalation = () => cancelSection('escalation');
  const cancelWorkingHours = () => cancelSection('workingHours');
  const cancelCompliance = () => cancelSection('compliance');

  // Check if section has changes
  const hasChanges = useCallback((sectionName) => {
    if (!originalConfig || !originalConfig[sectionName]) return false;
    return JSON.stringify(config[sectionName]) !== JSON.stringify(originalConfig[sectionName]);
  }, [config, originalConfig]);

  const updateConfig = useCallback((path, value) => {
    setConfig(prev => {
      const keys = path.split('.');

      // Create a shallow copy of the root object
      const newConfig = { ...prev };

      // Navigate to the parent object
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        // Create shallow copies along the path
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      // Set the final value
      current[keys[keys.length - 1]] = value;

      return newConfig;
    });
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111111]">
          <div className="h-5 bg-gray-200 dark:bg-[#222222] rounded w-40 animate-pulse"></div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-300 dark:border-gray-600 border-t-gray-900 dark:border-t-gray-100"></div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Loading configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111111]">
        <div className="max-w-4xl mx-auto px-6 py-3.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ai-dashboard')}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-sm font-semibold text-gray-900 dark:text-white">
                AI Configuration
              </h1>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Configure Iris AI for your business needs
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="space-y-3">
          {/* Main AI Assistant */}
          <Section
            id="mainAgent"
            title="Main AI Assistant (Iris)"
            description="Select an existing user mailbox to power as your main AI assistant"
            icon={SparklesIcon}
            isExpanded={expandedSections.mainAgent}
            onToggle={toggleSection}
            onSave={saveMainAgent}
            onCancel={cancelMainAgent}
            saving={savingSection === 'mainAgent'}
            hasChanges={hasChanges('mainAgent')}
          >
            <div className="space-y-5">
              {/* Selected AI Assistant Card */}
              {config.mainAgent?.userId && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] p-5">
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                        <SparklesIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {config.mainAgent.displayName || 'Iris AI'}
                        </h3>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          Active
                        </span>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {(() => {
                          const selectedUser = users.find(u => (u._id || u.id) === config.mainAgent.userId);
                          if (selectedUser) return selectedUser.email;
                          if (loadingUsers) return 'Loading user...';
                          return 'User not found';
                        })()}
                      </p>

                      <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-blue-500"></span>
                          iris-main
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-green-500"></span>
                          Enabled
                        </span>
                      </div>
                    </div>

                    {/* Change button */}
                    <button
                      type="button"
                      onClick={() => {
                        document.getElementById('ai-user-select')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
                    >
                      Change
                    </button>
                  </div>
                </div>
              )}

              {/* No user selected state */}
              {!config.mainAgent?.userId && (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#111111] p-6 text-center">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-[#222222] flex items-center justify-center mx-auto mb-3">
                    <SparklesIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    No AI Assistant Selected
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                    Select an existing mailbox user below to activate Iris AI
                  </p>
                </div>
              )}

              {/* Selection Fields */}
              <div className="space-y-4 pt-1">
                <div id="ai-user-select">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Select AI Assistant User <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    value={config.mainAgent?.userId || ''}
                    onChange={(value) => {
                      updateConfig('mainAgent.userId', value);
                      const selectedUser = users.find(u => (u._id || u.id) === value);
                      if (selectedUser) {
                        updateConfig('mainAgent.displayName', `${selectedUser.firstName} ${selectedUser.lastName}`.trim() || selectedUser.email);
                      }
                    }}
                    options={users.map(user => ({
                      value: user._id || user.id,
                      label: `${user.firstName} ${user.lastName} (${user.email})`
                    }))}
                    placeholder={loadingUsers ? 'Loading users...' : 'Select a user (e.g., ai@yourcompany.com)'}
                    disabled={loadingUsers}
                  />
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    Choose an existing mailbox user. We recommend a dedicated user like "ai@yourcompany.com".
                  </p>
                  {users.length === 0 && !loadingUsers && (
                    <p className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                      No users found. Please create a user mailbox first.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={config?.mainAgent?.displayName || ''}
                    onChange={(e) => updateConfig('mainAgent.displayName', e.target.value)}
                    placeholder="Iris AI"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all"
                  />
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    How the AI introduces itself in emails
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* Behavioral Guardrails */}
          <Section
            id="guardrails"
            title="Behavioral Guardrails"
            description="Define what Iris can and cannot do autonomously"
            icon={ShieldCheckIcon}
            isExpanded={expandedSections.guardrails}
            onToggle={toggleSection}
            onSave={saveGuardrails}
            onCancel={cancelGuardrails}
            saving={savingSection === 'guardrails'}
            hasChanges={hasChanges('guardrails')}
          >
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  ✅ Auto-Approve (No confirmation needed)
                </h3>
                <div className="space-y-2">
                  {[
                    { value: 'meetings', label: 'Book meetings' },
                    { value: 'internal_emails', label: 'Send internal emails' },
                    { value: 'basic_info', label: 'Answer basic information requests' },
                    { value: 'create_tasks', label: 'Create tasks and reminders' },
                    { value: 'search_knowledge', label: 'Search knowledge base' },
                    { value: 'status_updates', label: 'Send status updates' }
                  ].map(item => (
                    <label key={item.value} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={config?.guardrails?.autoApprove?.includes(item.value)}
                        onChange={(e) => {
                          const current = config?.guardrails?.autoApprove || [];
                          const updated = e.target.checked
                            ? [...current, item.value]
                            : current.filter(v => v !== item.value);
                          updateConfig('guardrails.autoApprove', updated);
                        }}
                        className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  ⚠️ Require Approval (Ask before doing)
                </h3>
                <div className="space-y-2">
                  {[
                    { value: 'external_emails', label: 'Send external emails' },
                    { value: 'financial_transactions', label: 'Process financial transactions' },
                    { value: 'access_grants', label: 'Grant access permissions' },
                    { value: 'data_changes', label: 'Modify or delete data' },
                    { value: 'contract_actions', label: 'Contract-related actions' },
                    { value: 'user_management', label: 'User management operations' }
                  ].map(item => (
                    <label key={item.value} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={config?.guardrails?.requireApproval?.includes(item.value)}
                        onChange={(e) => {
                          const current = config?.guardrails?.requireApproval || [];
                          const updated = e.target.checked
                            ? [...current, item.value]
                            : current.filter(v => v !== item.value);
                          updateConfig('guardrails.requireApproval', updated);
                        }}
                        className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>Note:</strong> Any action not listed in Auto-Approve or Require Approval will be blocked by default.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Spending Limit (USD)
                </label>
                <input
                  type="number"
                  value={config?.guardrails?.spendingLimit || 0}
                  onChange={(e) => updateConfig('guardrails.spendingLimit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Maximum amount Iris can approve without human confirmation
                </p>
              </div>
            </div>
          </Section>

          {/* Personality & Tone */}
          <Section
            id="personality"
            title="Personality & Tone"
            description="Define how Iris communicates with your team"
            icon={ChatBubbleLeftRightIcon}
            isExpanded={expandedSections.personality}
            onToggle={toggleSection}
            onSave={savePersonality}
            onCancel={cancelPersonality}
            saving={savingSection === 'personality'}
            hasChanges={hasChanges('personality')}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Communication Tone
                  </label>
                  <select
                    value={config?.personality?.tone || ''}
                    onChange={(e) => updateConfig('personality.tone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="formal">Formal</option>
                    <option value="casual">Casual</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Response Style
                  </label>
                  <select
                    value={config?.personality?.responseStyle || ''}
                    onChange={(e) => updateConfig('personality.responseStyle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  >
                    <option value="concise">Concise</option>
                    <option value="detailed">Detailed</option>
                    <option value="bullet_points">Bullet Points</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Instructions
                </label>
                <textarea
                  value={config?.personality?.customInstructions || ''}
                  onChange={(e) => updateConfig('personality.customInstructions', e.target.value)}
                  placeholder="Add company-specific guidelines for how Iris should behave..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Greeting Template
                </label>
                <textarea
                  value={config?.personality?.greetingTemplate || ''}
                  onChange={(e) => updateConfig('personality.greetingTemplate', e.target.value)}
                  placeholder="Hello! I'm Iris, your AI assistant. How can I help you today?"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                />
              </div>

              {/* Email Signature Section */}
              <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Email Signature</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Customize the signature used in AI-generated emails with rich formatting
                  </p>
                </div>

                <div className="space-y-3">
                  {/* Rich Text Signature Editor */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Signature Content
                    </label>
                    <div className="signature-editor-container">
                      <ReactQuill
                        value={config?.personality?.emailSignature || ''}
                        onChange={(content) => updateConfig('personality.emailSignature', content)}
                        placeholder="Best regards, Iris AI"
                        modules={{
                          toolbar: {
                            container: [
                              [{ 'header': [1, 2, 3, false] }],
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ 'color': [] }, { 'background': [] }],
                              [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                              [{ 'align': [] }],
                              ['link', 'image'],
                              ['clean']
                            ]
                          },
                          imageResize: {
                            parchment: Quill.import('parchment'),
                            modules: ['Resize', 'DisplaySize', 'Toolbar']
                          }
                        }}
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'color', 'background',
                          'list', 'bullet',
                          'align',
                          'link', 'image',
                          'width', 'height', 'style'
                        ]}
                        theme="snow"
                        className="bg-white dark:bg-[#2a2a2a] rounded-lg signature-quill-editor"
                        style={{ height: '250px' }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Use the toolbar to format text, add colors, links, and images to your signature
                    </p>
                  </div>
                </div>
              </div>

              <style jsx>{`
                .signature-quill-editor .ql-container {
                  height: 200px;
                  font-size: 14px;
                }

                .signature-quill-editor .ql-editor {
                  min-height: 200px;
                  color: #111827;
                }

                @media (prefers-color-scheme: dark) {
                  .signature-quill-editor .ql-editor {
                    color: #f9fafb;
                  }

                  .signature-quill-editor .ql-snow .ql-stroke {
                    stroke: #9ca3af;
                  }

                  .signature-quill-editor .ql-snow .ql-fill {
                    fill: #9ca3af;
                  }

                  .signature-quill-editor .ql-toolbar {
                    background: #374151;
                    border-color: #4b5563;
                  }

                  .signature-quill-editor .ql-container {
                    background: #1f2937;
                    border-color: #4b5563;
                  }

                  .signature-quill-editor .ql-editor.ql-blank::before {
                    color: #6b7280;
                  }
                }

                :global(.dark) .signature-quill-editor .ql-editor {
                  color: #f9fafb !important;
                }

                :global(.dark) .signature-quill-editor .ql-snow .ql-stroke {
                  stroke: #9ca3af;
                }

                :global(.dark) .signature-quill-editor .ql-snow .ql-fill {
                  fill: #9ca3af;
                }

                :global(.dark) .signature-quill-editor .ql-toolbar {
                  background: #374151;
                  border-color: #4b5563;
                }

                :global(.dark) .signature-quill-editor .ql-container {
                  background: #1f2937;
                  border-color: #4b5563;
                }

                :global(.dark) .signature-quill-editor .ql-editor.ql-blank::before {
                  color: #6b7280;
                }
              `}</style>
            </div>
          </Section>

          {/* Escalation Rules */}
          <Section
            id="escalation"
            title="Escalation Rules"
            description="Intelligent AI-powered escalation and routing"
            icon={BellAlertIcon}
            isExpanded={expandedSections.escalation}
            onToggle={toggleSection}
            onSave={saveEscalation}
            onCancel={cancelEscalation}
            saving={savingSection === 'escalation'}
            hasChanges={hasChanges('escalation')}
          >
            <div className="space-y-6">
              {/* Escalation Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Escalation Method
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'keyword', label: 'Keywords Only', desc: 'Simple keyword matching' },
                    { value: 'ai', label: 'AI Analysis', desc: 'Intelligent context-aware' },
                    { value: 'hybrid', label: 'Hybrid', desc: 'Both AI + keywords' }
                  ].map(method => (
                    <label key={method.value} className="relative flex flex-col p-3 border-2 rounded-lg cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors" style={{
                      borderColor: config?.escalation?.method === method.value ? '#111827' : '#d1d5db'
                    }}>
                      <input
                        type="radio"
                        name="escalation-method"
                        checked={config?.escalation?.method === method.value}
                        onChange={() => updateConfig('escalation.method', method.value)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {method.label}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {method.desc}
                      </span>
                      {config?.escalation?.method === method.value && (
                        <div className="absolute top-2 right-2 w-2 h-2 bg-gray-900 dark:bg-white rounded-full"></div>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* AI Escalation Settings */}
              {(config?.escalation?.method === 'ai' || config?.escalation?.method === 'hybrid') && (
                <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center gap-2 mb-4">
                    <SparklesIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      AI Analysis Configuration
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {/* Analysis Factors */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        What should AI analyze?
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: 'urgency_level', label: 'Urgency Level' },
                          { value: 'sentiment', label: 'Sentiment' },
                          { value: 'complexity', label: 'Complexity' },
                          { value: 'risk_level', label: 'Risk Level' },
                          { value: 'vip_status', label: 'VIP Status' },
                          { value: 'topic_sensitivity', label: 'Topic Sensitivity' },
                          { value: 'confidence', label: 'AI Confidence' },
                          { value: 'policy_violation', label: 'Policy Violations' }
                        ].map(factor => (
                          <label key={factor.value} className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              checked={config?.escalation?.aiEscalation?.analysisFactors?.includes(factor.value)}
                              onChange={(e) => {
                                const current = config?.escalation?.aiEscalation?.analysisFactors || [];
                                const updated = e.target.checked
                                  ? [...current, factor.value]
                                  : current.filter(f => f !== factor.value);
                                updateConfig('escalation.aiEscalation.analysisFactors', updated);
                              }}
                              className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-gray-700 dark:text-gray-300">{factor.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Escalation Triggers */}
                    <div className="border-t border-blue-200 dark:border-blue-700 pt-4">
                      <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Escalation Triggers
                      </h4>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Urgency Level */}
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Min Urgency Level
                          </label>
                          <select
                            value={config?.escalation?.aiEscalation?.triggers?.urgencyLevel || 'high'}
                            onChange={(e) => updateConfig('escalation.aiEscalation.triggers.urgencyLevel', e.target.value)}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                          >
                            <option value="medium">Medium+</option>
                            <option value="high">High+</option>
                            <option value="critical">Critical Only</option>
                          </select>
                        </div>

                        {/* Risk Level */}
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Min Risk Level
                          </label>
                          <select
                            value={config?.escalation?.aiEscalation?.triggers?.riskLevel || 'high'}
                            onChange={(e) => updateConfig('escalation.aiEscalation.triggers.riskLevel', e.target.value)}
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                          >
                            <option value="medium">Medium+</option>
                            <option value="high">High+</option>
                            <option value="critical">Critical Only</option>
                          </select>
                        </div>

                        {/* Sentiment Threshold */}
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Sentiment Threshold: {config?.escalation?.aiEscalation?.triggers?.sentimentThreshold || -0.5}
                          </label>
                          <input
                            type="range"
                            min="-1"
                            max="1"
                            step="0.1"
                            value={config?.escalation?.aiEscalation?.triggers?.sentimentThreshold || -0.5}
                            onChange={(e) => updateConfig('escalation.aiEscalation.triggers.sentimentThreshold', parseFloat(e.target.value))}
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Escalate if more negative than this
                          </p>
                        </div>

                        {/* Complexity Threshold */}
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Complexity Threshold: {config?.escalation?.aiEscalation?.triggers?.complexityThreshold || 75}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={config?.escalation?.aiEscalation?.triggers?.complexityThreshold || 75}
                            onChange={(e) => updateConfig('escalation.aiEscalation.triggers.complexityThreshold', parseInt(e.target.value))}
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Escalate if more complex than this
                          </p>
                        </div>

                        {/* Min Confidence */}
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Minimum AI Confidence: {config?.escalation?.aiEscalation?.triggers?.minConfidence || 70}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={config?.escalation?.aiEscalation?.triggers?.minConfidence || 70}
                            onChange={(e) => updateConfig('escalation.aiEscalation.triggers.minConfidence', parseInt(e.target.value))}
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Escalate if AI confidence is below this threshold
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Department Routing */}
                    <div className="border-t border-blue-200 dark:border-blue-800 pt-4">
                      <label className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={config?.escalation?.aiEscalation?.departmentRouting?.enabled || false}
                          onChange={(e) => updateConfig('escalation.aiEscalation.departmentRouting.enabled', e.target.checked)}
                          className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Enable AI-based Department Routing
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        AI will automatically route escalations to the appropriate department (HR, Legal, Finance, IT, etc.)
                      </p>

                      {/* Department Managers Configuration */}
                      {config?.escalation?.aiEscalation?.departmentRouting?.enabled && (
                        <div className="mt-4 border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
                          <div className="mb-3">
                            <h4 className="text-xs font-semibold text-gray-900 dark:text-white">
                              Department Managers
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Assign managers to departments for AI-based escalation routing
                            </p>
                          </div>

                          {/* Add New Department Manager Form */}
                          <div className="mb-4">
                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
                              Add New Department Manager
                            </h5>
                            <NewDepartmentManagerForm
                              users={users}
                              loadingUsers={loadingUsers}
                              onAdd={(newDepartment) => {
                                const departments = config?.escalation?.aiEscalation?.departmentRouting?.departments || [];

                                // Check if department already exists
                                const existingDept = departments.find(d => d.department === newDepartment.department);
                                if (existingDept) {
                                  showNotification?.(`A manager for ${newDepartment.department.toUpperCase()} department already exists. Please edit the existing one or remove it first.`, 'error');
                                  return;
                                }

                                // Add the new department
                                updateConfig('escalation.aiEscalation.departmentRouting.departments', [
                                  ...departments,
                                  newDepartment
                                ]);
                                showNotification?.('Department manager added. Click "Save Changes" to save to database.', 'success');
                              }}
                            />
                          </div>

                          {/* Saved Department Managers List */}
                          {(config?.escalation?.aiEscalation?.departmentRouting?.departments || []).length > 0 && (
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                              <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
                                Configured Department Managers ({(config?.escalation?.aiEscalation?.departmentRouting?.departments || []).length})
                              </h5>
                              <div className="space-y-3">
                                {(config?.escalation?.aiEscalation?.departmentRouting?.departments || []).map((dept, index) => {
                                  const selectedUser = users.find(u => u._id === dept.managerId || u.id === dept.managerId);
                                  return (
                                    <div key={`dept-${dept.department}-${dept.managerId}-${index}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-[#111111]">
                                      <div className="grid grid-cols-3 gap-3">
                                        {/* Department Type */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Department
                                          </label>
                                          <select
                                            value={dept.department || 'general'}
                                            onChange={(e) => {
                                              const departments = [...(config?.escalation?.aiEscalation?.departmentRouting?.departments || [])];
                                              departments[index] = { ...departments[index], department: e.target.value };
                                              updateConfig('escalation.aiEscalation.departmentRouting.departments', departments);
                                            }}
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                                          >
                                            <option value="hr">HR</option>
                                            <option value="finance">Finance</option>
                                            <option value="it">IT</option>
                                            <option value="sales">Sales</option>
                                            <option value="support">Support</option>
                                            <option value="legal">Legal</option>
                                            <option value="operations">Operations</option>
                                            <option value="marketing">Marketing</option>
                                            <option value="general">General</option>
                                          </select>
                                        </div>

                                        {/* Manager Selection */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Manager
                                          </label>
                                          <SearchableSelect
                                            value={dept.managerId || ''}
                                            onChange={(value) => {
                                              const departments = [...(config?.escalation?.aiEscalation?.departmentRouting?.departments || [])];
                                              departments[index] = { ...departments[index], managerId: value };
                                              updateConfig('escalation.aiEscalation.departmentRouting.departments', departments);
                                            }}
                                            options={users.map(user => ({
                                              value: user._id || user.id,
                                              label: `${user.firstName} ${user.lastName} (${user.email})`
                                            }))}
                                            placeholder={loadingUsers ? 'Loading users...' : users.length === 0 ? 'No users found' : 'Select manager'}
                                            disabled={loadingUsers || users.length === 0}
                                          />
                                        </div>

                                        {/* Designation */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Designation
                                          </label>
                                          <input
                                            type="text"
                                            value={dept.designation || ''}
                                            onChange={(e) => {
                                              const departments = [...(config?.escalation?.aiEscalation?.departmentRouting?.departments || [])];
                                              departments[index] = { ...departments[index], designation: e.target.value };
                                              updateConfig('escalation.aiEscalation.departmentRouting.departments', departments);
                                            }}
                                            placeholder="e.g., HR Manager"
                                            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                                          />
                                        </div>

                                        {/* Manager Info Display & Actions */}
                                        <div className="col-span-3 flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                                          <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                                            {selectedUser && (
                                              <>
                                                <span>✉️ {selectedUser.email}</span>
                                                {dept.designation && <span>• {dept.designation}</span>}
                                              </>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <label className="flex items-center gap-1.5">
                                              <input
                                                type="checkbox"
                                                checked={dept.enabled !== false}
                                                onChange={(e) => {
                                                  const departments = [...(config?.escalation?.aiEscalation?.departmentRouting?.departments || [])];
                                                  departments[index] = { ...departments[index], enabled: e.target.checked };
                                                  updateConfig('escalation.aiEscalation.departmentRouting.departments', departments);
                                                }}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                              />
                                              <span className="text-xs text-gray-700 dark:text-gray-300">Active</span>
                                            </label>
                                            <button
                                              onClick={() => {
                                                const departments = [...(config?.escalation?.aiEscalation?.departmentRouting?.departments || [])];
                                                departments.splice(index, 1);
                                                updateConfig('escalation.aiEscalation.departmentRouting.departments', departments);
                                              }}
                                              className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Legacy Keyword Settings (for hybrid/keyword mode) */}
              {(config?.escalation?.method === 'keyword' || config?.escalation?.method === 'hybrid') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Urgency Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={config?.escalation?.urgencyKeywords?.join(', ') || ''}
                    onChange={(e) => updateConfig('escalation.urgencyKeywords', e.target.value.split(',').map(k => k.trim()))}
                    placeholder="urgent, asap, emergency, critical"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {config?.escalation?.method === 'hybrid'
                      ? 'These keywords will trigger immediate escalation in addition to AI analysis'
                      : 'Emails containing these words will be immediately escalated'}
                  </p>
                </div>
              )}

              {/* VIP Emails */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  VIP Email Addresses (one per line)
                </label>
                <textarea
                  value={config?.escalation?.vipEmails?.join('\n') || ''}
                  onChange={(e) => updateConfig('escalation.vipEmails', e.target.value.split('\n').filter(e => e.trim()))}
                  placeholder="ceo@company.com&#10;board@company.com"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Emails from these addresses are always escalated with highest priority
                </p>
              </div>

              {/* Escalation Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Auto-escalation Time: {config?.escalation?.escalationTimeHours || 4} hours
                </label>
                <input
                  type="range"
                  min="1"
                  max="24"
                  step="1"
                  value={config?.escalation?.escalationTimeHours || 4}
                  onChange={(e) => updateConfig('escalation.escalationTimeHours', parseInt(e.target.value))}
                  className="w-full"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Escalate to human if no response received within this timeframe
                </p>
              </div>
            </div>
          </Section>

          {/* Working Hours & SLAs */}
          <Section
            id="workingHours"
            title="Working Hours & SLAs"
            description="Response time targets and after-hours behavior"
            icon={ClockIcon}
            isExpanded={expandedSections.workingHours}
            onToggle={toggleSection}
            onSave={saveWorkingHours}
            onCancel={cancelWorkingHours}
            saving={savingSection === 'workingHours'}
            hasChanges={hasChanges('workingHours')}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  AI Response Time Targets
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Set maximum response times for AI to reply to emails (in minutes)
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Urgent/Critical
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0.5"
                        max="60"
                        step="0.5"
                        value={config?.workingHours?.responseTargets?.urgent || 1}
                        onChange={(e) => updateConfig('workingHours.responseTargets.urgent', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 pr-16 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">
                        minutes
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Recommended: 0.5-2 min
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Standard Priority
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="120"
                        step="1"
                        value={config?.workingHours?.responseTargets?.standard || 4}
                        onChange={(e) => updateConfig('workingHours.responseTargets.standard', parseInt(e.target.value))}
                        className="w-full px-3 py-2 pr-16 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">
                        minutes
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Recommended: 3-10 min
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Low Priority
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="5"
                        max="1440"
                        step="5"
                        value={config?.workingHours?.responseTargets?.low || 24}
                        onChange={(e) => updateConfig('workingHours.responseTargets.low', parseInt(e.target.value))}
                        className="w-full px-3 py-2 pr-16 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400">
                        minutes
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Recommended: 10-30 min
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  After-Hours Behavior
                </label>
                <select
                  value={config?.workingHours?.afterHoursBehavior || ''}
                  onChange={(e) => updateConfig('workingHours.afterHoursBehavior', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                >
                  <option value="auto_respond">Auto-respond only</option>
                  <option value="emergency_only">Emergency handling only</option>
                  <option value="full_service">Full service 24/7</option>
                  <option value="off">Completely off</option>
                </select>
              </div>
            </div>
          </Section>

          {/* Compliance & Privacy */}
          <Section
            id="compliance"
            title="Compliance & Privacy"
            description="Data handling and regulatory compliance"
            icon={LockClosedIcon}
            isExpanded={expandedSections.compliance}
            onToggle={toggleSection}
            onSave={saveCompliance}
            onCancel={cancelCompliance}
            saving={savingSection === 'compliance'}
            hasChanges={hasChanges('compliance')}
          >
            <div className="space-y-4">
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.compliance?.gdpr || false}
                    onChange={(e) => updateConfig('compliance.gdpr', e.target.checked)}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    GDPR Compliance Mode
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.compliance?.hipaa || false}
                    onChange={(e) => updateConfig('compliance.hipaa', e.target.checked)}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    HIPAA Compliance Mode
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.compliance?.auditLogging || false}
                    onChange={(e) => updateConfig('compliance.auditLogging', e.target.checked)}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Enable Audit Logging
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Retention Period (days)
                </label>
                <input
                  type="number"
                  value={config?.compliance?.dataRetentionDays || 90}
                  onChange={(e) => updateConfig('compliance.dataRetentionDays', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  How long to keep conversation history and logs
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  PII Handling Mode
                </label>
                <select
                  value={config?.compliance?.piiHandling || ''}
                  onChange={(e) => updateConfig('compliance.piiHandling', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
                >
                  <option value="standard">Standard</option>
                  <option value="redact">Redact PII from logs</option>
                  <option value="encrypt">Encrypt PII in storage</option>
                  <option value="strict">Strict (minimal PII collection)</option>
                </select>
              </div>
            </div>
          </Section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIConfiguration;
